import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './paid-student-progress.css';

const serverBase = (process.env.REACT_APP_API_URL || 'http://localhost:5030/api').replace(/\/api\/?$/, '');

const mediaUrl = (value) => {
  const path = String(value || '').trim();
  if (!path) return '';
  return /^(https?:|data:|blob:)/i.test(path)
    ? path
    : `${serverBase}${path.startsWith('/') ? '' : '/'}${path}`;
};

const courseIcon = (course) => {
  const text = `${course?.courseName || ''} ${course?.category || ''}`.toLowerCase();
  if (/python/.test(text)) return 'bi-filetype-py';
  if (/data|analytics/.test(text)) return 'bi-bar-chart-line-fill';
  if (/ai|machine|ml/.test(text)) return 'bi-cpu-fill';
  if (/design|ui|ux|figma/.test(text)) return 'bi-palette-fill';
  if (/cloud|aws/.test(text)) return 'bi-cloud-fill';
  if (/security|cyber/.test(text)) return 'bi-shield-lock-fill';
  if (/java|mern|web|full.?stack|develop/.test(text)) return 'bi-code-slash';
  return 'bi-mortarboard-fill';
};

const normalize = (value) => String(value || '').trim().toLowerCase();
const clampPercent = (value) => Math.max(0, Math.min(100, Math.round(Number(value) || 0)));

const tokensFor = (course) => {
  const ignored = new Set(['and', 'the', 'with', 'course', 'program', 'training', 'advanced', 'certificate']);
  return new Set(normalize(`${course?.courseName || ''} ${course?.category || ''}`)
    .split(/[^a-z0-9+#.]+/)
    .filter((token) => token.length > 1 && !ignored.has(token)));
};

const relationScore = (candidate, anchor) => {
  if (!anchor) return 0;
  let score = normalize(candidate?.category) && normalize(candidate?.category) === normalize(anchor?.category) ? 8 : 0;
  const anchorTokens = tokensFor(anchor);
  tokensFor(candidate).forEach((token) => {
    if (anchorTokens.has(token)) score += 2;
  });
  return score;
};

const catalogRows = (response) => {
  const payload = response?.data || {};
  const rows = Array.isArray(payload.records) ? payload.records : payload.data;
  return Array.isArray(rows) ? rows : (Array.isArray(rows?.records) ? rows.records : []);
};

function CourseArtwork({ course }) {
  const image = mediaUrl(course?.thumbnailUrl || course?.promoImageUrl);
  return (
    <div className="paid-progress-course-art" style={{ '--progress-accent': course?.courseColor || '#4f46e5' }}>
      {image
        ? <img src={image} alt="" />
        : <i className={`bi ${courseIcon(course)}`} aria-hidden="true" />}
    </div>
  );
}

export default function PaidStudentProgressPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [progressResult, catalogResult] = await Promise.all([
        api.get('/dashboard/training-institute/student'),
        api.get('/training/memberships/catalog').catch(() => null),
      ]);
      setData(progressResult.data?.data || {});
      setCatalog(catalogRows(catalogResult));
    } catch (requestError) {
      setData({});
      setCatalog([]);
      setError(requestError.response?.data?.message || 'Could not load your learning progress.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const courses = useMemo(() => (Array.isArray(data?.myCourses) ? data.myCourses : []), [data?.myCourses]);
  const activity = useMemo(() => (Array.isArray(data?.learningActivity) ? data.learningActivity.slice(-7) : []), [data?.learningActivity]);
  const totalLessons = courses.reduce((sum, course) => sum + Number(course.totalLessons || 0), 0);
  const completedLessons = courses.reduce((sum, course) => sum + Number(course.completedLessons || 0), 0);
  const activeDays = activity.filter((day) => Number(day.courseActivity || 0) > 0).length;
  const enrolledIds = useMemo(() => new Set(courses.map((course) => String(course.courseId || '')).filter(Boolean)), [courses]);
  const anchorCourse = useMemo(() => {
    const completed = courses.filter((course) => course.status === 'completed' || Number(course.progress || 0) >= 100);
    return completed[completed.length - 1]
      || [...courses].sort((a, b) => Number(b.progress || 0) - Number(a.progress || 0))[0]
      || null;
  }, [courses]);
  const recommendation = useMemo(() => catalog
    .filter((course) => !enrolledIds.has(String(course._id || '')))
    .map((course) => ({ course, score: relationScore(course, anchorCourse) }))
    .sort((a, b) => b.score - a.score)[0] || null, [anchorCourse, catalog, enrolledIds]);

  const openCourse = (course) => {
    const courseId = course?.courseId;
    const query = courseId ? `?courseId=${encodeURIComponent(courseId)}` : '';
    navigate(`/course-content${query}`);
  };

  if (loading && !data) {
    return (
      <div className="paid-progress-state">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
        Loading your progress...
      </div>
    );
  }

  return (
    <main className="paid-student-progress-page">
      <header className="paid-progress-header">
        <div>
          <span className="paid-progress-kicker">Your learning journey</span>
          <h1>Learning Progress</h1>
          <p>Track completed lessons and continue from where you stopped.</p>
        </div>
        <button type="button" className="paid-progress-refresh" onClick={load} aria-label="Refresh progress" title="Refresh progress">
          <i className="bi bi-arrow-clockwise" aria-hidden="true" />
        </button>
      </header>

      {error && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between gap-3" role="alert">
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={load}>Retry</button>
        </div>
      )}

      <section className="paid-progress-overview" aria-labelledby="progress-overview-title">
        <div className="paid-progress-ring" style={{ '--progress-value': `${clampPercent(data?.avgProgress)}%` }}>
          <div><strong>{clampPercent(data?.avgProgress)}%</strong><span>overall</span></div>
        </div>
        <div className="paid-progress-overview-copy">
          <span className="paid-progress-kicker">Overall completion</span>
          <h2 id="progress-overview-title">Keep your course momentum visible</h2>
          <p>Your progress is calculated from completed topics in your enrolled courses.</p>
        </div>
        <div className="paid-progress-metrics">
          <div><i className="bi bi-journal-bookmark-fill" /><strong>{Number(data?.enrollments?.total || 0)}</strong><span>Enrolled</span></div>
          <div><i className="bi bi-play-circle-fill" /><strong>{Number(data?.enrollments?.inProgress || 0)}</strong><span>In progress</span></div>
          <div><i className="bi bi-check-circle-fill" /><strong>{Number(data?.enrollments?.completed || 0)}</strong><span>Completed</span></div>
          <div><i className="bi bi-list-check" /><strong>{completedLessons}/{totalLessons}</strong><span>Lessons</span></div>
        </div>
      </section>

      <section className="paid-progress-section" aria-labelledby="course-progress-title">
        <div className="paid-progress-section-heading">
          <div>
            <span className="paid-progress-kicker">Current courses</span>
            <h2 id="course-progress-title">Course Progress</h2>
          </div>
          <span className="paid-progress-count">{courses.length} course{courses.length === 1 ? '' : 's'}</span>
        </div>

        {courses.length ? (
          <div className="paid-progress-course-list">
            {courses.map((course) => {
              const progress = clampPercent(course.progress);
              return (
                <article className="paid-progress-course" key={String(course._id || course.courseId)}>
                  <CourseArtwork course={course} />
                  <div className="paid-progress-course-copy">
                    <div className="paid-progress-course-topline">
                      <span>{course.category || course.deliveryMode || 'Course'}</span>
                      <strong>{progress}%</strong>
                    </div>
                    <h3>{course.courseName || 'Enrolled course'}</h3>
                    <div className="paid-progress-bar" role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
                      <span style={{ width: `${progress}%` }} />
                    </div>
                    <div className="paid-progress-course-meta">
                      <span><i className="bi bi-check2-circle" /> {Number(course.completedLessons || 0)} of {Number(course.totalLessons || 0)} lessons</span>
                      {course.batchNumber && <span><i className="bi bi-people" /> {course.batchNumber}</span>}
                    </div>
                  </div>
                  <button type="button" className="paid-progress-course-action" onClick={() => openCourse(course)}>
                    {progress > 0 ? 'Continue' : 'Start course'} <i className="bi bi-arrow-right" />
                  </button>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="paid-progress-empty">
            <i className="bi bi-journal-x" aria-hidden="true" />
            <div><strong>No enrolled course progress yet</strong><span>Your enrolled courses will appear here.</span></div>
          </div>
        )}
      </section>

      {activity.length > 0 && (
        <section className="paid-progress-section paid-progress-activity" aria-labelledby="activity-title">
          <div className="paid-progress-section-heading">
            <div>
              <span className="paid-progress-kicker">Last seven days</span>
              <h2 id="activity-title">Learning Activity</h2>
            </div>
            <span className="paid-progress-count">{activeDays} active day{activeDays === 1 ? '' : 's'}</span>
          </div>
          <div className="paid-progress-activity-grid">
            {activity.map((day) => {
              const active = Number(day.courseActivity || 0) > 0;
              return (
                <div className={active ? 'is-active' : ''} key={day.key} title={`${day.courseActivity || 0} learning activities`}>
                  <span>{new Date(`${day.key}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short' }).slice(0, 1)}</span>
                  <i className={`bi ${active ? 'bi-check-lg' : 'bi-dash'}`} aria-hidden="true" />
                  <small>{day.label}</small>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {recommendation?.course && anchorCourse && (
        <section className="paid-progress-recommendation" aria-labelledby="next-course-title">
          <CourseArtwork course={recommendation.course} />
          <div className="paid-progress-recommendation-copy">
            <span className="paid-progress-kicker">Suggested next course</span>
            <h2 id="next-course-title">{recommendation.course.courseName}</h2>
            <p>
              {recommendation.score > 0
                ? `A relevant next step after ${anchorCourse.courseName}.`
                : `Continue building your profile after ${anchorCourse.courseName}.`}
            </p>
            <div className="paid-progress-recommendation-meta">
              {recommendation.course.category && <span><i className="bi bi-grid" /> {recommendation.course.category}</span>}
              {Number(recommendation.course.totalLessons || 0) > 0 && <span><i className="bi bi-play-btn" /> {recommendation.course.totalLessons} lessons</span>}
            </div>
          </div>
          <button type="button" onClick={() => navigate('/dashboard')}>
            View course <i className="bi bi-arrow-right" />
          </button>
        </section>
      )}
    </main>
  );
}
